import { z } from 'zod';

export enum DomainComplianceStatus {
  Approved = 'APPROVED',
  Pending = 'PENDING',
  Rejected = 'REJECTED',
  NotAvailable = 'NOT_AVAILABLE',
  Unknown = 'UNKNOWN',
}

export enum DomainComplianceDemandPartner {
  Magnite = 'MAGNITE',
  Pubmatic = 'PUBMATIC',
}

export enum DomainsSortProperty {
  Publisher = 'publisher',
  Url = 'url',
}

export enum DomainsSortDirection {
  Ascending = 'ASC',
  Descending = 'DESC',
}

export enum PublisherDomainStatus {
  Active = 'ACTIVE',
  Archived = 'ARCHIVED',
}

export function isDomainsSortProperty(value: string): value is DomainsSortProperty {
  return Object.values(DomainsSortProperty).includes(value as DomainsSortProperty);
}

const entityIdSchema = z.union([z.string(), z.number()]).transform(String);
const countSchema = z
  .number()
  .nullish()
  .transform((value) => value ?? 0);
const complianceStatusSchema = z.preprocess(
  (value) =>
    typeof value === 'string' &&
    Object.values(DomainComplianceStatus).includes(value as DomainComplianceStatus)
      ? value
      : DomainComplianceStatus.Unknown,
  z.enum(DomainComplianceStatus),
);

export const domainComplianceSummarySchema = z.looseObject({
  publisherName: z.string(),
  url: z.string(),
  approvedCnt: countSchema,
  pendingCnt: countSchema,
  rejectedCnt: countSchema,
  notAvailableCnt: countSchema,
});

export const domainsPageSchema = z.looseObject({
  content: z.array(domainComplianceSummarySchema),
  totalElements: z.number(),
});

export const publisherDomainListItemSchema = z
  .looseObject({
    id: z.looseObject({
      domainId: z.union([z.string(), z.number()]),
      publisherId: z.union([z.string(), z.number()]),
    }),
    status: z.enum(PublisherDomainStatus),
    createdOn: z.string().nullish(),
    updatedOn: z.string().nullish(),
    domain: z.looseObject({
      url: z.string(),
      lastTritonCpm: z.number().nullish(),
      todayTritonCpm: z.number().nullish(),
      last7DaysTritonCpm: z
        .array(z.object({ date: z.string(), tritonCpm: z.number().nullish() }))
        .nullish(),
      createdBy: z.looseObject({ name: z.string().nullish() }).nullish(),
    }),
    last7DaysTritonCpm: z
      .array(z.object({ date: z.string(), tritonCpm: z.number().nullish() }))
      .nullish(),
  })
  .transform((item) => ({
    domainId: String(item.id.domainId),
    publisherId: String(item.id.publisherId),
    source: item,
    lastTritonCpm: item.domain.lastTritonCpm ?? null,
    last7DaysTritonCpm: item.domain.last7DaysTritonCpm ?? item.last7DaysTritonCpm ?? [],
    url: item.domain.url,
    status: item.status,
    createdBy: item.domain.createdBy?.name ?? '-',
    createdOn: item.createdOn ?? undefined,
    updatedOn: item.updatedOn ?? undefined,
  }));

export const publisherDomainsPageSchema = z.looseObject({
  content: z.array(publisherDomainListItemSchema),
  totalElements: z.number(),
});

export const domainComplianceApprovalSchema = z.looseObject({
  id: z
    .looseObject({
      url: z.string().nullish(),
      demandPartnerId: entityIdSchema.nullish(),
    })
    .nullish(),
  demandPartner: z.looseObject({
    id: entityIdSchema.nullish(),
    code: z.string().nullish(),
    displayName: z.string(),
    status: z.string().nullish(),
  }),
  status: complianceStatusSchema,
  allowed: z.boolean().nullish(),
  submitted: z.boolean().nullish(),
  submittedOn: z.string().nullish(),
  comment: z.string().nullish(),
  dppSiteId: z.string().nullish(),
  dppZoneId: z.string().nullish(),
  dppPublisherId: z.string().nullish(),
  dppTagId: z.string().nullish(),
  url: z.string().nullish(),
  demandPartnerId: entityIdSchema.nullish(),
});

export const domainComplianceApprovalsSchema = z.array(domainComplianceApprovalSchema);

export const domainPublisherOptionSchema = z.looseObject({
  id: entityIdSchema,
  name: z.string(),
});

export const domainPublisherPageSchema = z.looseObject({
  content: z.array(domainPublisherOptionSchema),
  totalElements: z.number(),
});

export type DomainComplianceSummary = z.infer<typeof domainComplianceSummarySchema>;
export type DomainComplianceApproval = z.infer<typeof domainComplianceApprovalSchema>;
export type DomainPublisherOption = z.infer<typeof domainPublisherOptionSchema>;
export type PublisherDomainListItem = z.infer<typeof publisherDomainListItemSchema>;

export enum PublisherDomainsAddMethod {
  Single = 'SINGLE',
  Multiple = 'MULTIPLE',
}

export enum PublisherDomainImmuContentType {
  Collections = 'COLLECTIONS',
  AiVideo = 'AI_VIDEO',
}

export enum PublisherDomainTtsVoice {
  JoannaStandard = 'Joanna_standard',
  JoannaNeural = 'Joanna_neural',
  RuthNeural = 'Ruth_neural',
  GregoryNeural = 'Gregory_neural',
  StephenNeural = 'Stephen_neural',
}

export interface PublisherDomainCreatePayload {
  id: { publisherId: string };
  domain: { url: string; todayTritonCpm?: number | null };
  playlistIds: string[] | null;
  immuEnabled: boolean;
  immuHasContent: boolean;
  immuEnableMonetization: boolean;
  immuContentType: PublisherDomainImmuContentType | null;
  immuAdsPreRoll: boolean;
  immuAdsMidRoll: boolean;
  immuAdsPostRoll: boolean;
  immuAdTag: string;
  scrapingTitleSelector: string;
  scrapingContentSelector: string;
  scrapingImageSelector: string;
  scrapingExcludedSelector: string;
  ttsVoiceType: PublisherDomainTtsVoice;
}

export interface DomainsFilter {
  searchTerm: string;
  publisherIds: string[];
  page: number;
  size: number;
  sortProperty: DomainsSortProperty | null;
  sortDirection: DomainsSortDirection;
}

export interface PublisherDomainsFilter {
  searchTerm: string;
  statuses: PublisherDomainStatus[];
  page: number;
  size: number;
}

export interface DomainComplianceMutationPayload {
  approvals: DomainComplianceApproval[];
}

export const domainsPageSizes = [5, 10, 25, 50, 100] as const;

export const defaultDomainsFilter: DomainsFilter = {
  searchTerm: '',
  publisherIds: [],
  page: 0,
  size: 10,
  sortProperty: DomainsSortProperty.Publisher,
  sortDirection: DomainsSortDirection.Ascending,
};

export const defaultPublisherDomainsFilter: PublisherDomainsFilter = {
  searchTerm: '',
  statuses: [],
  page: 0,
  size: 10,
};

export function domainComplianceTotal(summary: DomainComplianceSummary) {
  return summary.approvedCnt + summary.pendingCnt + summary.rejectedCnt + summary.notAvailableCnt;
}

export function isDomainComplianceDemandPartner(
  approval: DomainComplianceApproval,
  partner: DomainComplianceDemandPartner,
) {
  const identifier = approval.demandPartner.code ?? approval.demandPartner.id;
  return identifier?.trim().toUpperCase() === partner;
}

export function serializeDomainApprovals(
  approvals: readonly DomainComplianceApproval[],
): DomainComplianceMutationPayload {
  return {
    approvals: approvals.map((approval) => ({
      ...approval,
      url: approval.id?.url ?? approval.url ?? null,
      demandPartnerId:
        approval.id?.demandPartnerId ??
        approval.demandPartnerId ??
        approval.demandPartner.id ??
        null,
    })),
  };
}
